import {
  RENTAL_DEFAULT_MIN_DURATION,
  RENTAL_DEFAULT_TIMESLOT_INTERVAL,
} from '../models/constants';

import {
  TimeWorkingMode,
  TimeOption,
  TimeDMOConfig,
} from '../models/entities/timeslot.model';
import { BusinessHoursPeriod } from '../graphql/schema';

import DateService from './date.service';

export default class RentalService {
  static addMinutesForDatetime(
    index: number,
    openingDate: number,
    generatedHour: Date,
    minutes: number
  ): Date {
    return index === 0
      ? new Date(openingDate)
      : new Date(generatedHour.getTime() + minutes * 60000);
  }

  static generateAvailableHours(
    openingTime = '09:00',
    closingTime = '18:00',
    type: 'start' | 'end',
    selectedRangeTime: TimeWorkingMode,
    timeDMOConfig: TimeDMOConfig,
    shouldApplySameDate?: boolean
  ): TimeOption[] {
    if (
      Number(openingTime.substring(0, 2)) > Number(closingTime.substring(0, 2))
    )
      return [];

    const openingDate = new Date().setHours(
      Number(openingTime.slice(0, 2)),
      Number(openingTime.slice(-2))
    );
    const closingDate = new Date().setHours(
      Number(closingTime.slice(0, 2)),
      Number(closingTime.slice(-2))
    );
    const timeDifference = (closingDate - openingDate) / (1000 * 3600);

    return RentalService.generateSlotHoursArray(
      timeDifference,
      openingDate,
      type,
      selectedRangeTime,
      timeDMOConfig,
      shouldApplySameDate
    );
  }

  static generateHourForDisable(
    generatedHour: Date,
    type: 'start' | 'end',
    minimumDuration: number
  ): Date {
    const hour = new Date(generatedHour);
    if (type === 'start') hour.setMinutes(hour.getMinutes() + minimumDuration);
    if (type === 'end') hour.setMinutes(hour.getMinutes() - minimumDuration);
    return hour;
  }

  static generateSlotHoursArray(
    timeDifference: number,
    openingDate: number,
    type: 'start' | 'end',
    selectedRangeTime: TimeWorkingMode,
    timeDMOConfig: TimeDMOConfig,
    shouldApplySameDate?: boolean
  ): TimeOption[] {
    const generatedHours: TimeOption[] = [];
    const { timeSlotInterval, minimumDuration } = timeDMOConfig;
    let generatedHour = new Date();
    let termFound = false;
    const minutesTimeSlotInterval =
      timeSlotInterval || RENTAL_DEFAULT_TIMESLOT_INTERVAL;
    const minutesMinimumDuration =
      minimumDuration || RENTAL_DEFAULT_MIN_DURATION;

    const convertMin = DateService.getMinToHours(minutesTimeSlotInterval);
    for (let index = 0; index <= timeDifference / convertMin; index += 1) {
      generatedHour = RentalService.addMinutesForDatetime(
        index,
        openingDate,
        generatedHour,
        minutesTimeSlotInterval
      );
      const value = RentalService.generateValueForDisable(generatedHour);
      const hourForDisable = RentalService.generateHourForDisable(
        generatedHour,
        type,
        minutesMinimumDuration
      );
      const valueForDisable =
        RentalService.generateValueForDisable(hourForDisable);

      if (type === 'start') {
        generatedHours.push({
          value,
          disabled: RentalService.isTimePickerDisabled(
            termFound,
            type,
            shouldApplySameDate
          ),
        });
        if (valueForDisable >= selectedRangeTime?.end?.value) termFound = true;
      } else {
        if (valueForDisable >= selectedRangeTime?.start?.value)
          termFound = true;
        generatedHours.push({
          value,
          disabled: RentalService.isTimePickerDisabled(
            termFound,
            type,
            shouldApplySameDate
          ),
        });
      }
    }

    return generatedHours;
  }

  static generateValueForDisable(generatedHour: Date): string {
    return `${`0${generatedHour.getHours()}`.slice(
      -2
    )}:${`0${generatedHour.getMinutes()}`.slice(-2)}`;
  }

  static isTimePickerDisabled(
    termFound: boolean,
    type?: 'start' | 'end',
    shouldApplySameDate?: boolean
  ): boolean {
    if (shouldApplySameDate) {
      if (type === 'start') {
        return termFound;
      }
      if (type === 'end') {
        return !termFound;
      }
    }
    return false;
  }

  static setAvailableTime(
    businessHoursPeriods: BusinessHoursPeriod,
    selectedRangeTime: TimeWorkingMode,
    type: 'start' | 'end',
    timeDMOConfig: TimeDMOConfig,
    shouldApplySameDate: boolean
  ): TimeOption[] {
    const { closingTime, openingTime } = businessHoursPeriods;
    return RentalService.generateAvailableHours(
      openingTime,
      closingTime,
      type,
      selectedRangeTime,
      timeDMOConfig,
      shouldApplySameDate
    );
  }
}
